import type { FormScope, FormVersion, Subform, SubformEmailConfiguration } from "../state/formVersion";
import { useFormVersionDispatch, useFormVersionState } from "../state/formVersion";
import type { SetStoreFunction } from "solid-js/store";
import { initialized } from "../utils/initializedUtil";
import SubformId from "../state/SubformId";
import { type SubformFieldProperties } from "../fields/subform/SubformField";
import { type PinFieldProperties } from "../fields/media/PinField";
import FormServicesFactory from "./FormServicesFactory";
import { type Accessor, batch, createMemo, on } from "solid-js";
import { useTranslation } from "../state/i18n";
import type { TFunction } from "i18next";
import { cloneDeep, isNil, some, uniq, uniqBy } from "lodash-es";
import type FieldId from "../fields/FieldId";
import type { FieldConfig } from "../fields/types";
import { trackStore } from "@solid-primitives/deep";
import type { ValueOf } from "../types";
import { Scope } from "../types";
import FieldService from "./FieldService";
import { registerSubformExistsCheck } from "../utils/validation/idReferences";
import type { StringTemplateTypes } from "../utils/stringTemplateUtil.ts";
import FieldValueReference from "../fields/FieldValueReference.ts";

type NestedFieldConfig = FieldConfig<SubformFieldProperties | PinFieldProperties>;

let setFormVersionStore: SetStoreFunction<FormVersion> | null = null;
let formVersionStore: FormVersion | null = null;
let t: TFunction | null = null;
let usageMap: Accessor<Map<string, NestedFieldConfig[]>> | null = null;

const getFormVersionStore = () => initialized(formVersionStore, "formVersionStore");
const getSetFormVersionStore = () => initialized(setFormVersionStore, "setFormVersionStore");
const getT = () => initialized(t, "t");

const init = () => {
  setFormVersionStore = useFormVersionDispatch();
  formVersionStore = useFormVersionState();
  t = useTranslation().t;
  registerSubformExistsCheck((id) => !isNil(getSubform(id)));

  // Pre-calculate this expensive operation instead of doing it per-subform
  // oxlint-disable-next-line solid/reactivity
  usageMap = createMemo(
    on(
      () => trackStore(getFormVersionStore().fields),
      (fields) =>
        getUsedBy(fields.filter((field) => field.type === "subform" || field.type === "pin") as NestedFieldConfig[]),
    ),
  );
};

const getUsedBy = (nestedFields: NestedFieldConfig[]) => {
  const usageMap = new Map<string, NestedFieldConfig[]>();

  nestedFields.forEach((field) => {
    const addToUsageMap = (targetFormId: string, field: NestedFieldConfig) => {
      if (!usageMap.has(targetFormId)) {
        usageMap.set(targetFormId, []);
      }
      usageMap.get(targetFormId)!.push(field);
    };

    if (field.type === "subform") {
      const subformProperties = field.properties as SubformFieldProperties;
      if (!isNil(subformProperties.target_form_id)) {
        addToUsageMap(subformProperties.target_form_id.toString(), field);
      }
    } else if (field.type === "pin") {
      const pinProperties = field.properties as PinFieldProperties;
      pinProperties.pins.forEach((pin) => {
        if (!isNil(pin.target_form_id)) {
          addToUsageMap(pin.target_form_id.toString(), field);
        }
      });
    }
  });
  return usageMap;
};

const getSubforms = () => getFormVersionStore().subforms;
const getSubform = (id: SubformId) => getSubforms().find((subform) => subform.uid.equals(id));
const usedBy = (id: SubformId): NestedFieldConfig[] => {
  if (isNil(usageMap?.())) {
    return [];
  }
  return usageMap()?.get(id.toString()) ?? [];
};

const getAllowedSubforms = (scope: FormScope) => {
  if (scope === Scope.ROOT) {
    return getFormVersionStore().subforms;
  }

  const parents = getAllParents(scope).map((parent) => parent.toString()) as string[];
  return getFormVersionStore().subforms.filter((subform) => !parents.includes(subform.uid.toString()));
};

const getSubformsReachableFrom = (scope: FormScope, visited: Set<SubformId> = new Set()): Subform[] => {
  const { fieldService } =
    scope === Scope.ROOT ? FormServicesFactory.forRootForm() : FormServicesFactory.forSubform(scope);
  const allSubformFields = fieldService.getFieldsByType<SubformFieldProperties>("subform");
  const allPinFields = fieldService.getFieldsByType<PinFieldProperties>("pin");
  const subformIds = [
    ...allSubformFields.map((field) => field.properties.target_form_id),
    ...allPinFields.flatMap((field) => field.properties.pins.map((pin) => pin.target_form_id)),
  ];

  const currentSubform = scope !== Scope.ROOT ? getSubform(scope) : undefined;

  const subformsInCurrentScope = subformIds
    .filter((v) => !isNil(v))
    .filter((v) => !some([...visited], (visitedSubform) => v.equals(visitedSubform)))
    .map(getSubform)
    .filter((v) => !isNil(v));

  subformsInCurrentScope.forEach((x) => visited.add(x.uid));

  const subformsInLowerScope = subformsInCurrentScope.flatMap((subform) =>
    getSubformsReachableFrom(subform.uid, visited),
  );

  return uniq([
    ...(!isNil(currentSubform) ? [currentSubform] : []),
    ...subformsInCurrentScope,
    ...subformsInLowerScope,
  ]);
};

const getScopedParents = (
  scope: SubformId,
  subformFields: FieldConfig<SubformFieldProperties | PinFieldProperties>[],
): { scope: FormScope; subform?: Subform }[] => {
  const currentSubform = getSubform(scope);
  if (isNil(currentSubform)) {
    return [];
  }

  const fields = subformFields.filter((field) => {
    if (field.type === "subform") {
      const subformProperties = field.properties as SubformFieldProperties;
      return scope.equals(subformProperties.target_form_id);
    } else if (field.type === "pin") {
      const pinProperties = field.properties as PinFieldProperties;
      return pinProperties.pins.some((pin) => scope.equals(pin.target_form_id));
    }
    return false;
  });

  const parentSubforms = fields
    .filter((v) => !isNil(v))
    .map((field) =>
      field.scope === Scope.ROOT
        ? { scope: Scope.ROOT as FormScope }
        : { scope: field.scope, subform: getSubform(field.scope as SubformId) },
    )
    .filter((field) => !isNil(field));

  return uniqBy([{ scope, subform: currentSubform }, ...parentSubforms], (subform) => subform.scope);
};

const getAllParents = (scope: SubformId): FormScope[] => {
  const parents: FormScope[] = [];
  const visitedScopes = new Set<string>();
  const fields = FieldService.getFieldsByTypes<SubformFieldProperties | PinFieldProperties>(["subform", "pin"]);

  const collectParents = (currentScope: SubformId | FormScope) => {
    if (currentScope === Scope.ROOT || visitedScopes.has(currentScope.toString())) {
      return;
    }

    visitedScopes.add(currentScope.toString());
    parents.push(currentScope);
    const currentParents = getScopedParents(currentScope as SubformId, fields);

    currentParents.forEach((parent) => {
      if (!visitedScopes.has(parent.scope.toString())) {
        parents.push(parent.scope);
        collectParents(parent.scope);
      }
    });
  };

  collectParents(scope);
  return uniqBy(parents, (parent) => parent.toString());
};

export const defaultEmailConfiguration: SubformEmailConfiguration = {
  content: [],
  pinFieldsToAttach: [],
  pdfContent: "",
  simpleMode: true,
  hideNoValues: false,
  horizontalOrientation: false,
};

const updateReference = (changedReferences: Map<string, FieldId>) => (stringTemplatePart: StringTemplateTypes) => {
  if (stringTemplatePart.type === "field") {
    const newFieldId = changedReferences.get(stringTemplatePart.value.id.toString());
    if (!isNil(newFieldId)) {
      return {
        ...stringTemplatePart,
        value: FieldValueReference.from(newFieldId.toString(), stringTemplatePart.value.type),
      };
    }
  }
  return stringTemplatePart;
};

const SubformService = {
  init,
  getSubforms,
  getSubform,
  getSubformsReachableFrom,
  getScopedParents: (scope: SubformId) =>
    getScopedParents(
      scope,
      FieldService.getFieldsByTypes<SubformFieldProperties | PinFieldProperties>(["subform", "pin"]),
    ),
  getAllowedSubforms,
  createSubform: (name: string): Subform => {
    const subform: Subform = {
      uid: SubformId.random(),
      meta: {
        name,
      },
      emailConfiguration: { ...defaultEmailConfiguration },
    };
    getSetFormVersionStore()("subforms", (prev) => [...prev, subform]);
    return subform;
  },
  addSubform: (subform: Subform) => {
    getSetFormVersionStore()("subforms", (prev) => [...prev, subform]);
  },
  deleteSubform: (id: SubformId) => {
    batch(() => {
      const { fieldService, ruleService } = FormServicesFactory.forSubform(id);

      usedBy(id).forEach((field) => {
        if (field.type === "subform") {
          fieldService.setFieldProperty(field.uid, "target_form_id", null);
        } else if (field.type === "pin") {
          const pinField = cloneDeep(field as FieldConfig<PinFieldProperties>);
          const pins = pinField.properties.pins.map((pin) => {
            if (id.equals(pin.target_form_id)) {
              pin.target_form_id = null;
            }

            return pin;
          });

          fieldService.setFieldProperty(field.uid, "pins", pins);
        }
      });

      fieldService.getFields().forEach((field) => {
        fieldService.removeField(field.uid);
      });
      ruleService.getAll().forEach((rule) => {
        ruleService.delete(rule.id);
      });

      getSetFormVersionStore()("subforms", (prev) => prev.filter((subform) => !subform.uid.equals(id)));
    });
  },
  duplicateSubform: (id: SubformId) => {
    const subform = getSubform(id);
    if (isNil(subform)) {
      return;
    }

    const clonedSubform: Subform = {
      ...cloneDeep(subform),
      uid: SubformId.random(),
      meta: {
        name: `${subform?.meta.name} ${getT()(($) => $.SUBFORMS_DUPLICATE_FORM_NAME_SUFFIX)}`,
      },
    };

    batch(() => {
      getSetFormVersionStore()("subforms", (prev) => [...prev, clonedSubform]);

      const changedReferences = new Map<string, FieldId>();
      FormServicesFactory.forSubform(id)
        .fieldService.getFields()
        .forEach((field) => {
          const newFieldId = FormServicesFactory.forSubform(clonedSubform.uid).fieldService.duplicateField(
            field.uid,
            clonedSubform.uid,
          );
          changedReferences.set(field.uid.toString(), newFieldId);
        });

      FormServicesFactory.forSubform(clonedSubform.uid)
        .fieldService.getFields()
        .forEach((field) => {
          FormServicesFactory.forSubform(clonedSubform.uid).fieldService.duplicateFieldProperties(
            field.uid,
            updateReference(changedReferences),
          );
        });

      FormServicesFactory.forSubform(id)
        .ruleService.getAll()
        .forEach((rule) => {
          FormServicesFactory.forSubform(clonedSubform.uid).ruleService.duplicate(
            rule.id,
            changedReferences,
            clonedSubform.uid,
          );
        });

      if (!isNil(subform.emailConfiguration) && !isNil(clonedSubform.emailConfiguration)) {
        clonedSubform.emailConfiguration.content = subform.emailConfiguration.content.map(
          updateReference(changedReferences),
        );
      }
    });
  },
  updateSubformName: (id: SubformId, name: string) => {
    getSetFormVersionStore()("subforms", (subform) => subform.uid.equals(id), "meta", "name", name);
  },
  updateSubformId: (id: SubformId, newId: SubformId) => {
    getSetFormVersionStore()("subforms", (subform) => subform.uid.equals(id), "uid", newId);
  },
  addEmailConfiguration: (id: SubformId) => {
    getSetFormVersionStore()("subforms", (subform) => subform.uid.equals(id), "emailConfiguration", {
      ...defaultEmailConfiguration,
      pinFieldsToAttach: FormServicesFactory.forSubform(id)
        .fieldService.getFieldsByType<PinFieldProperties>("pin")
        .map((field) => field.uid),
    });
  },
  updateEmailConfiguration: (id: SubformId, emailConfiguration: SubformEmailConfiguration) => {
    getSetFormVersionStore()("subforms", (subform) => subform.uid.equals(id), "emailConfiguration", emailConfiguration);
  },
  deleteEmailConfiguration: (id: SubformId) =>
    getSetFormVersionStore()("subforms", (subform) => subform.uid.equals(id), "emailConfiguration", undefined),
  updateEmailConfigurationProperty: (
    id: SubformId,
    property: keyof SubformEmailConfiguration,
    value: ValueOf<SubformEmailConfiguration>,
  ) => getSetFormVersionStore()("subforms", (subform) => subform.uid.equals(id), "emailConfiguration", property, value),
  usedBy,
  hasErrorsInSubform: (id: SubformId) => {
    const { validationService } = FormServicesFactory.forRootForm();
    return validationService.getSubformErrors().some((e) => e.subformId.equals(id));
  },
};

export default SubformService;
